import ChevronRight from '@mui/icons-material/ChevronRight';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import clsx from 'clsx';
import { memo, useCallback, useMemo, useState } from 'react';

import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { flattenAll, flattenHierarchy, flattenSearchTree, tagSearchMatches } from './hierarchy-navigator-utils.js';

import type { DesignHierarchyNode } from '@cadstrom/sigma-engine-shared';
import type { FlatNode, MatchTag } from './hierarchy-navigator-utils.js';

export type HierarchyNavigatorSelection = {
	designId: string;
	instanceId?: string;
};

export type HierarchyNavigatorProps = {
	hierarchy: DesignHierarchyNode[];
	/** Currently selected design + instance */
	selectedDesignId: string | null;
	selectedInstanceId?: string;
	/** Called when user selects a node */
	onSelect: (selection: HierarchyNavigatorSelection) => void;
	className?: string;
};

export const HierarchyNavigator = memo(function HierarchyNavigator({
	hierarchy,
	selectedDesignId,
	selectedInstanceId,
	onSelect,
	className,
}: HierarchyNavigatorProps) {
	const tag = useDataElTagGenerator(HierarchyNavigator);
	// Tracks nodes whose expand/collapse state has been flipped from their default.
	// Nodes default to expanded unless all children share the parent's designId (multi-instance).
	const [toggledKeys, setToggledKeys] = useState<Set<string>>(new Set());
	const [searchText, setSearchText] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const [highlightedKey, setHighlightedKey] = useState<string | null>(null);

	const isSearching = isOpen && searchText.length > 0;

	const treeNodes = useMemo(
		() => flattenHierarchy(hierarchy, 0, undefined, undefined, toggledKeys),
		[hierarchy, toggledKeys],
	);

	const searchTreeNodes = useMemo(() => {
		if (!isSearching) {
			return [];
		}
		const tags = new Map<DesignHierarchyNode, MatchTag>();
		tagSearchMatches(hierarchy, searchText.toLowerCase(), tags, false);
		return flattenSearchTree(hierarchy, tags, 0, undefined, undefined);
	}, [isSearching, hierarchy, searchText]);

	const visibleOptions = isSearching ? searchTreeNodes : treeNodes;

	const firstMatchKey = useMemo(() => {
		if (!isSearching) {
			return null;
		}
		return searchTreeNodes.find((n) => n.matchType === 'match' && !n.isFolder && n.designId !== undefined)?.key ?? null;
	}, [isSearching, searchTreeNodes]);

	// Flatten everything unconditionally for selectedNode lookup
	const allFlatNodes = useMemo(() => flattenAll(hierarchy, 0, undefined, undefined), [hierarchy]);

	const selectedNode = useMemo(() => {
		return (
			allFlatNodes.find((n) => n.designId === selectedDesignId && n.instanceId === selectedInstanceId) ??
			allFlatNodes.find((n) => n.designId === selectedDesignId && !n.instanceId) ??
			undefined
		);
	}, [allFlatNodes, selectedDesignId, selectedInstanceId]);

	const toggleExpand = useCallback((key: string, event: React.MouseEvent) => {
		event.stopPropagation();
		event.preventDefault();
		setToggledKeys((prev) => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	}, []);

	const filterOptions = useCallback(
		(options: FlatNode[], { inputValue: query }: { inputValue: string }) => {
			// During search, options are already pre-filtered by flattenSearchTree
			if (isSearching || !query) {
				return options;
			}
			const lower = query.toLowerCase();
			return options.filter((opt) => opt.label.toLowerCase().includes(lower));
		},
		[isSearching],
	);

	return (
		<Autocomplete
			openOnFocus
			clearOnBlur
			clearOnEscape
			autoComplete
			open={isOpen}
			onOpen={() => {
				setIsOpen(true);
				setSearchText('');
			}}
			onClose={() => {
				setIsOpen(false);
				setSearchText('');
				setHighlightedKey(null);
			}}
			onHighlightChange={(_event, option) => {
				setHighlightedKey(option?.key ?? null);
			}}
			options={visibleOptions}
			value={selectedNode}
			inputValue={searchText}
			onInputChange={(_event, newInputValue, reason) => {
				// Reset event is fired when the input is initially selected. By default it sets the value to the value of the current selection,
				// which causes the tree view to filter out all of the sheets which isn't super usable.
				if (reason === 'reset') {
					setSearchText('');
					return;
				}

				setSearchText(newInputValue);
			}}
			getOptionKey={(option) => option.key}
			getOptionLabel={(option) => (isOpen ? option.fullPath : '')}
			isOptionEqualToValue={(option, value) =>
				option.designId === value.designId && option.instanceId === value.instanceId
			}
			filterOptions={filterOptions}
			className={clsx(className, 'rounded-lg border border-gray-a6')}
			sx={{
				'& .MuiOutlinedInput-notchedOutline': { border: 'none !important' },
				'& .MuiInput-underline::before, & .MuiInput-underline::after': { border: 'none !important' },
				'& .MuiInputBase-root': { padding: '2px 8px' },
			}}
			onChange={(_event, newValue) => {
				if (newValue && !newValue.isFolder && newValue.designId !== undefined) {
					onSelect({
						designId: newValue.designId,
						instanceId: newValue.instanceId,
					});
					setSearchText('');
				} else if (!newValue) {
					setSearchText('');
				}
			}}
			getOptionDisabled={(option) => {
				if (option.isFolder) {
					return true;
				}
				if (isSearching && option.matchType !== 'match') {
					return true;
				}
				return false;
			}}
			renderInput={(params) => {
				const pathPrefix =
					!isOpen && selectedNode && selectedNode.fullPath !== selectedNode.label ?
						selectedNode.fullPath.slice(0, selectedNode.fullPath.length - selectedNode.label.length)
					:	undefined;

				return (
					<TextField
						{...params}
						placeholder={!isOpen && selectedNode ? '' : 'Search sheets...'}
						slotProps={{
							input: {
								...params.InputProps,
								...(!isOpen && selectedNode ?
									{
										startAdornment: (
											<span
												{...tag('selected-path')}
												className='pointer-events-none flex items-center overflow-hidden p-1 whitespace-nowrap'
											>
												{pathPrefix && (
													<span
														className='min-w-0 shrink overflow-hidden opacity-40'
														style={{ direction: 'rtl', textOverflow: 'ellipsis' }}
													>
														<bdi>{pathPrefix}</bdi>
													</span>
												)}
												<span className='shrink-0'>
													{pathPrefix && '\u00a0'}
													{selectedNode.label}
												</span>
											</span>
										),
									}
								:	{}),
							},
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter' && isSearching && !highlightedKey) {
								const firstMatch = searchTreeNodes.find(
									(n) => n.matchType === 'match' && !n.isFolder && n.designId !== undefined,
								);
								if (firstMatch) {
									onSelect({ designId: firstMatch.designId!, instanceId: firstMatch.instanceId });
									return;
								}
							}

							// On backspace or escape, if the input is empty, clear the search and open
							if ((e.key === 'Backspace' || e.key === 'Escape') && searchText === '') {
								setIsOpen(true);
							}

							params.inputProps.onKeyDown?.(e as React.KeyboardEvent<HTMLInputElement>);
						}}
					/>
				);
			}}
			renderOption={({ key, ...props }: React.HTMLAttributes<HTMLLIElement> & { key: string }, option: FlatNode) => {
				const isSelected =
					!option.isFolder && option.designId === selectedDesignId && option.instanceId === selectedInstanceId;
				const isExpanded = !isSearching && option.hasChildren && treeNodes.some((n) => n.parentKey === option.key);
				const isMuted = option.matchType === 'ancestor' || (option.isFolder && !isSearching);
				const isDefaultMatch = isSearching && !highlightedKey && option.key === firstMatchKey;
				// Descendants are MUI-disabled (for keyboard skip) but still clickable
				const isClickableDescendant =
					isSearching && option.matchType === 'descendant' && !option.isFolder && option.designId !== undefined;

				return (
					<li
						key={key}
						{...props}
						{...tag('option')}
						aria-disabled={option.isFolder || isClickableDescendant ? undefined : props['aria-disabled']}
						className={clsx(
							props.className,
							isSelected && 'font-semibold',
							isMuted && 'opacity-50',
							isDefaultMatch && 'Mui-focused',
						)}
						onClick={
							option.isFolder && option.hasChildren && !isSearching ? (e) => toggleExpand(option.key, e)
							: isClickableDescendant ?
								() => {
									onSelect({ designId: option.designId!, instanceId: option.instanceId });
								}
							:	props.onClick
						}
					>
						<span style={{ width: option.depth * 20, flexShrink: 0 }} />
						{option.hasChildren && !option.isFolder ?
							<IconButton
								size='small'
								className={clsx('-my-2 mr-1 shrink-0', isSearching ? 'opacity-30' : 'hover:bg-gray-a4')}
								onClick={isSearching ? undefined : (e) => toggleExpand(option.key, e)}
								onMouseDown={(e) => e.preventDefault()}
								{...tag('expand-btn')}
							>
								{isSearching || isExpanded ?
									<ExpandMore fontSize='small' />
								:	<ChevronRight fontSize='small' />}
							</IconButton>
						:	<span className='mr-1 w-5 shrink-0' />}
						<span
							{...tag('label')}
							className={clsx('min-w-0 text-ellipsis whitespace-nowrap', option.isFolder && 'italic')}
						>
							{option.label}
						</span>
					</li>
				);
			}}
			slotProps={{
				listbox: {
					sx: {
						'& .MuiAutocomplete-option': {
							minHeight: 36,
							overflow: 'visible',
						},
						'& .MuiAutocomplete-option[aria-selected="true"]': {
							backgroundColor: 'var(--color-blue-4) !important',
							color: 'var(--color-blue-11)',
							fontWeight: 600,
						},
						'& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
							backgroundColor: 'var(--color-blue-5) !important',
						},
					},
				},
			}}
			{...tag()}
		/>
	);
});
